/**
 * ExchangeRatesPanel.js
 *
 * Recrea el look de swiftmoney.cubasolution.com / "Tasas de Cambio".
 * Visible para CEO (editar), Proveedor CU (editar) y Gestor (solo lectura).
 * El gestor ve las mismas tasas — los cálculos de remesas se guían de acá.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeftRight, Plus, Pencil, Trash2, Tag } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCIES = ['UYU', 'USD', 'USDT', 'MLC', 'CUP', 'EUR'];
const DEPOSITS = [
  { value: 'cash',     label: 'Depósito en Efectivo' },
  { value: 'transfer', label: 'Depósito por Transferencia' },
  { value: 'crypto',   label: 'Depósito en Cripto' },
];

const CURRENCY_TONE = {
  UYU: 'bg-blue-100 text-blue-700',
  USD: 'bg-emerald-100 text-emerald-700',
  USDT: 'bg-emerald-100 text-emerald-700',
  MLC: 'bg-emerald-100 text-emerald-700',
  CUP: 'bg-emerald-100 text-emerald-700',
  EUR: 'bg-amber-100 text-amber-700',
};

const ExchangeRatesPanel = () => {
  const { getAuthHeaders, user } = useAuth();
  const canEdit = user?.role === 'admin'
    || (user?.role === 'payment_agent' && (user?.country || '').toUpperCase() === 'CU');

  const [exchange, setExchange] = useState([]);
  const [promo, setPromo] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog state — one for regular, one for promotional.
  const [dlg, setDlg] = useState(null); // { kind: 'exchange'|'promo', mode: 'create'|'edit', payload }

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/rates/exchange`, { headers: getAuthHeaders() }),
        axios.get(`${API}/rates/promotional`, { headers: getAuthHeaders() }),
      ]);
      setExchange((r1.data || []).filter((r) => r.is_active !== false));
      setPromo(r2.data || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudieron cargar las tasas'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = (kind) =>
    setDlg({ kind, mode: 'create', payload: kind === 'exchange'
      ? { from_currency: 'UYU', to_currency: 'USD', rate: '' }
      : { from_currency: 'UYU', to_currency: 'MLC', rate: '', deposit_type: 'transfer', label: '' },
    });
  const openEdit = (kind, item) => setDlg({ kind, mode: 'edit', payload: { ...item, rate: item.rate ?? '' } });
  const close = () => setDlg(null);

  const save = async () => {
    if (!dlg) return;
    const { kind, mode, payload } = dlg;
    const numeric = parseFloat(payload.rate);
    if (Number.isNaN(numeric) || numeric <= 0) {
      toast.error('Ingresá una tasa válida (> 0)');
      return;
    }
    const url = kind === 'exchange'
      ? (mode === 'create' ? `${API}/rates/exchange` : `${API}/rates/exchange/${payload.id}`)
      : (mode === 'create' ? `${API}/rates/promotional` : `${API}/rates/promotional/${payload.id}`);
    const body = mode === 'create'
      ? { ...payload, rate: numeric }
      : { rate: numeric, ...(kind === 'promo' ? { deposit_type: payload.deposit_type, label: payload.label } : {}) };
    try {
      await (mode === 'create' ? axios.post(url, body, { headers: getAuthHeaders() })
                                : axios.put(url, body, { headers: getAuthHeaders() }));
      toast.success(mode === 'create' ? 'Tasa creada' : 'Tasa actualizada');
      close();
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo guardar'));
    }
  };

  const removeItem = async (kind, id) => {
    if (!window.confirm('¿Eliminar esta tasa?')) return;
    const url = kind === 'exchange' ? `${API}/rates/exchange/${id}` : `${API}/rates/promotional/${id}`;
    try {
      await axios.delete(url, { headers: getAuthHeaders() });
      toast.success('Tasa eliminada');
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo eliminar'));
    }
  };

  const RateCard = ({ kind, item }) => (
    <div className="flex items-center justify-between bg-white rounded-xl border border-slate-100 p-4 shadow-sm" data-testid={`rate-card-${item.id}`}>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <Badge className={`${CURRENCY_TONE[item.from_currency] || 'bg-slate-100 text-slate-600'} rounded-full px-3 py-1 font-bold`}>{item.from_currency}</Badge>
          <ArrowLeftRight className="w-4 h-4 text-slate-400" />
          <Badge className={`${CURRENCY_TONE[item.to_currency] || 'bg-slate-100 text-slate-600'} rounded-full px-3 py-1 font-bold`}>{item.to_currency}</Badge>
          {kind === 'promo' && item.label && (
            <span className="text-[11px] text-slate-400 ml-2">· {item.label}</span>
          )}
        </div>
        <p className="text-2xl font-bold text-slate-800">
          1 {item.from_currency} = <span className="font-mono">{Number(item.rate).toLocaleString('es-ES', { minimumFractionDigits: 4, maximumFractionDigits: 6 })}</span> {item.to_currency}
        </p>
        {kind === 'promo' && (
          <Badge variant="outline" className="mt-2 text-[10px] font-normal">
            {DEPOSITS.find((d) => d.value === item.deposit_type)?.label || 'Depósito'}
          </Badge>
        )}
      </div>
      {canEdit && (
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => openEdit(kind, item)} data-testid={`edit-rate-${item.id}`}>
            <Pencil className="w-4 h-4 text-slate-500" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => removeItem(kind, item.id)} data-testid={`del-rate-${item.id}`}>
            <Trash2 className="w-4 h-4 text-rose-500" />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-6 p-1" data-testid="exchange-rates-panel">
      {/* ── Tasas de Cambio ── */}
      <Card className="bg-slate-50/60 p-5 border-0 shadow-sm rounded-2xl">
        <div className="flex items-center gap-2 mb-1">
          <ArrowLeftRight className="w-5 h-5 text-blue-500" />
          <h1 className="text-2xl font-bold text-slate-800">Tasas de Cambio</h1>
        </div>
        <p className="text-sm text-slate-500 mb-4">Gestione las tasas de cambio entre monedas. Todas las remesas se guían de aquí.</p>
        {canEdit && (
          <Button onClick={() => openCreate('exchange')} className="w-full h-12 bg-blue-600 hover:bg-blue-700 rounded-xl text-base mb-4" data-testid="add-exchange-rate">
            <Plus className="w-5 h-5 mr-1" /> Agregar Tasa de Cambio
          </Button>
        )}
        <div className="space-y-3">
          {loading ? (
            <p className="text-center text-slate-400 text-sm py-4">Cargando…</p>
          ) : exchange.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-4">Aún no hay tasas configuradas.</p>
          ) : exchange.map((it, idx) => <RateCard key={it.id || `ex-${it.pair}-${idx}`} kind="exchange" item={it} />)}
        </div>
      </Card>

      {/* ── Tasas de Promoción ── */}
      <Card className="bg-violet-50/30 p-5 border-0 shadow-sm rounded-2xl">
        <div className="flex items-center gap-2 mb-1">
          <Tag className="w-5 h-5 text-blue-500" />
          <h1 className="text-2xl font-bold text-slate-800">Tasas de Promoción</h1>
        </div>
        <p className="text-sm text-slate-500 mb-4">Gestione las tasas de cambio promocionales por tipo de depósito.</p>
        {canEdit && (
          <Button onClick={() => openCreate('promo')} className="w-full h-12 bg-blue-600 hover:bg-blue-700 rounded-xl text-base mb-4" data-testid="add-promo-rate">
            <Plus className="w-5 h-5 mr-1" /> Agregar Tasa Promocional
          </Button>
        )}
        <div className="space-y-3">
          {loading ? null : promo.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-4">Sin tasas promocionales.</p>
          ) : promo.map((it, idx) => <RateCard key={it.id || `pr-${it.pair}-${idx}`} kind="promo" item={it} />)}
        </div>
      </Card>

      {/* ── Editor dialog ── */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dlg?.mode === 'create' ? 'Nueva tasa' : 'Editar tasa'}
              {dlg?.kind === 'promo' && ' promocional'}
            </DialogTitle>
          </DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>De</Label>
                  <Select
                    value={dlg.payload.from_currency}
                    onValueChange={(v) => setDlg({ ...dlg, payload: { ...dlg.payload, from_currency: v } })}
                    disabled={dlg.mode === 'edit'}
                  >
                    <SelectTrigger data-testid="from-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>A</Label>
                  <Select
                    value={dlg.payload.to_currency}
                    onValueChange={(v) => setDlg({ ...dlg, payload: { ...dlg.payload, to_currency: v } })}
                    disabled={dlg.mode === 'edit'}
                  >
                    <SelectTrigger data-testid="to-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Tasa (1 {dlg.payload.from_currency} = X {dlg.payload.to_currency})</Label>
                <Input
                  type="number" step="any" min="0"
                  value={dlg.payload.rate}
                  onChange={(e) => setDlg({ ...dlg, payload: { ...dlg.payload, rate: e.target.value } })}
                  data-testid="rate-input"
                  autoFocus
                />
              </div>
              {dlg.kind === 'promo' && (
                <>
                  <div className="space-y-1">
                    <Label>Tipo de depósito</Label>
                    <Select
                      value={dlg.payload.deposit_type}
                      onValueChange={(v) => setDlg({ ...dlg, payload: { ...dlg.payload, deposit_type: v } })}
                    >
                      <SelectTrigger data-testid="deposit-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DEPOSITS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Etiqueta (opcional)</Label>
                    <Input
                      value={dlg.payload.label || ''}
                      onChange={(e) => setDlg({ ...dlg, payload: { ...dlg.payload, label: e.target.value } })}
                      placeholder="Ej: Promo fin de mes"
                      data-testid="label-input"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-rate-btn">
              {dlg?.mode === 'create' ? 'Crear' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExchangeRatesPanel;
